import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bot, Zap, Shield, Server, Rocket, Users, 
  ChevronRight, Check, X, ArrowRight, Menu, X as XIcon,
  MessageSquare, BarChart3, Globe, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import api from '@/utils/api';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navbar Component
function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Features', href: '#features' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'FAQ', href: '#faq' },
  ];

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/90 backdrop-blur-xl border-b border-zinc-800' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png" 
            alt="TortLab" 
            className="w-8 h-8 object-contain"
          />
          <span className="font-['Outfit'] font-bold text-xl text-white">TortLab</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-sm text-zinc-400 hover:text-white transition-colors uppercase tracking-widest"
              data-testid={`nav-${link.name.toLowerCase()}`}
            >
              {link.name}
            </a>
          ))}
        </div>

        {/* Auth Buttons */}
        <div className="hidden md:flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/login')}
            className="text-zinc-400 hover:text-white hover:bg-transparent text-sm uppercase tracking-widest"
            data-testid="nav-login"
          >
            Login
          </Button>
          <Button 
            onClick={() => navigate('/register')}
            className="bg-white text-black hover:bg-zinc-200 rounded-none h-10 px-6 text-sm font-semibold uppercase tracking-wide"
            data-testid="nav-signup"
          >
            Sign Up
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <XIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="md:hidden bg-black border-t border-zinc-800"
        >
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block text-sm text-zinc-400 hover:text-white uppercase tracking-widest"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <div className="pt-4 space-y-2 border-t border-zinc-800">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="w-full justify-start text-zinc-400 hover:text-white"
              >
                Login
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="w-full bg-white text-black hover:bg-zinc-200 rounded-none"
              >
                Sign Up
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}

// Hero Section
function HeroSection() {
  const navigate = useNavigate();
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/50 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.03)_0%,_transparent_70%)]" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8 text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 text-sm"
            >
              <Zap className="w-4 h-4" />
              <span>Now with Multi-Agent Workflows</span>
            </motion.div>

            {/* Headline */}
            <h1 className="font-['Outfit'] text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-none">
              The OS for
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-b from-white to-zinc-500">
                AI Agencies
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-zinc-400 max-w-xl leading-relaxed">
              Deploy, manage, and scale autonomous AI chat agents for your business. 
              Built for agencies and enterprises who demand excellence.
            </p>

            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 pt-4"
            >
              <Button 
                onClick={() => navigate('/register')}
                className="bg-white text-black hover:bg-zinc-200 rounded-none h-14 px-10 text-sm font-semibold uppercase tracking-widest w-full sm:w-auto"
                data-testid="hero-cta-primary"
              >
                Start Building
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="bg-transparent border border-zinc-700 text-white hover:bg-zinc-900 hover:border-zinc-600 rounded-none h-14 px-10 text-sm font-semibold uppercase tracking-widest w-full sm:w-auto"
                data-testid="hero-cta-secondary"
              >
                View Pricing
              </Button>
            </motion.div>

            {/* Trust Badge */}
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-sm text-zinc-600 pt-4"
            >
              Trusted by many businesses worldwide
            </motion.p>
          </motion.div>

          {/* Right Content - Tablet Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex justify-center items-center"
          >
            <div className="relative">
              {/* Glow effect behind tablet */}
              <div className="absolute inset-0 bg-gradient-to-r from-zinc-800/50 to-zinc-700/30 blur-3xl scale-110" />
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/35f2eb62-f2f6-4b48-9388-e2c1ee261a61/images/20fc534fb8488a7924e4958467d75a1d93cb58d43db858b77098a997e1237f71.png"
                alt="AI Chat Agent on Tablet"
                className="relative w-full max-w-lg drop-shadow-2xl"
                data-testid="hero-tablet-image"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div 
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border border-zinc-700 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1 h-2 bg-zinc-500 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// Features Section
function FeaturesSection() {
  const features = [
    {
      icon: Bot,
      title: 'Autonomous Agents',
      description: 'Deploy intelligent AI workers that handle customer inquiries 24/7 without human intervention.',
      details: [
        'Fully configurable context with plain text, documents, and website data',
        'Connect to Google Business, Yelp, and other review platforms',
        'Product catalog scraping for e-commerce integration',
        'Multiple response styles: Professional, Friendly, or Fun',
        'Custom guardrails and topic controls'
      ],
      span: 'md:col-span-2 md:row-span-2',
      expanded: true
    },
    {
      icon: Shield,
      title: 'Multi-Tenant Security',
      description: 'Built for agencies. Isolate client data securely with role-based access control.',
      span: 'md:col-span-1'
    },
    {
      icon: Rocket,
      title: 'Instant Deploy',
      description: 'Go live in minutes. No complex setup or infrastructure management.',
      span: 'md:col-span-1'
    },
    {
      icon: MessageSquare,
      title: 'Smart Responses',
      description: 'Rich AI responses with images, links, and formatted content. Powered by the latest language models.',
      span: 'md:col-span-1'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Track performance, conversation metrics, and agent effectiveness in real-time.',
      span: 'md:col-span-1'
    },
    {
      icon: Globe,
      title: 'Public Chat Widget',
      description: 'Embed chat agents anywhere with a simple URL or QR code. Custom branding and logos supported.',
      span: 'md:col-span-2'
    }
  ];

  return (
    <section id="features" className="bg-black py-24 md:py-32" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-4">Capabilities</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold text-white tracking-tight">
            Everything you need to scale
          </h2>
        </motion.div>

        {/* Features Grid */}
        <motion.div 
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={fadeInUp}
              className={`bg-zinc-900/50 border border-zinc-800 p-8 hover:border-zinc-700 transition-all duration-300 hover:-translate-y-1 ${feature.span}`}
              data-testid={`feature-${index}`}
            >
              <feature.icon className="w-10 h-10 text-white mb-6" strokeWidth={1.5} />
              <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-3">{feature.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{feature.description}</p>
              {feature.details && (
                <ul className="mt-4 space-y-2">
                  {feature.details.map((detail, dIndex) => (
                    <li key={dIndex} className="flex items-start gap-2 text-sm text-zinc-500">
                      <Check className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Pricing Section
function PricingSection() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/pricing-plans');
        // API returns {plans: [...]} so extract the array
        const plansData = response.data?.plans || response.data || [];
        setPlans(Array.isArray(plansData) ? plansData : []);
      } catch (error) {
        console.error('Failed to fetch pricing plans:', error);
        // Fallback pricing data
        setPlans([
          { plan_id: 'starter', name: 'Starter', price: 29, billing_period: 'month', features: ['1 AI Agent', '1,000 Messages/mo', 'Email Support', 'Basic Analytics'] },
          { plan_id: 'professional', name: 'Professional', price: 99, billing_period: 'month', features: ['5 AI Agents', '10,000 Messages/mo', 'Priority Support', 'Advanced Analytics', 'Custom Branding'], is_popular: true },
          { plan_id: 'enterprise', name: 'Enterprise', price: 299, billing_period: 'month', features: ['Unlimited Agents', 'Unlimited Messages', 'Dedicated Support', 'Full Analytics', 'White Label', 'API Access'] }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  return (
    <section id="pricing" className="bg-zinc-950 py-24 md:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-4">Pricing</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold text-white tracking-tight">
            Simple, transparent pricing
          </h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
            Choose the plan that fits your needs. All plans include a 14-day free trial.
          </p>
        </motion.div>

        {/* Pricing Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-800"
          >
            {plans.map((plan, index) => (
              <div
                key={plan.plan_id || index}
                className={`p-10 flex flex-col border-b md:border-b-0 md:border-r last:border-r-0 border-zinc-800 ${
                  plan.is_popular ? 'bg-zinc-900' : 'bg-black/20'
                } hover:bg-zinc-900/50 transition-colors`}
                data-testid={`pricing-plan-${index}`}
              >
                {plan.is_popular && (
                  <span className="text-xs uppercase tracking-widest text-white bg-zinc-800 px-3 py-1 w-fit mb-6">
                    Most Popular
                  </span>
                )}
                <h3 className="font-['Outfit'] text-2xl font-semibold text-white">{plan.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="font-['Outfit'] text-5xl font-bold text-white">${plan.monthly_price || plan.price || 0}</span>
                  <span className="text-zinc-500">/{plan.billing_period || 'month'}</span>
                </div>
                <p className="text-zinc-400 text-sm mb-4">{plan.description}</p>
                <ul className="space-y-4 flex-1">
                  {(plan.features || []).map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3 text-zinc-400">
                      <Check className="w-4 h-4 text-white flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {/* Show additional plan details as features if no features array */}
                  {(!plan.features || plan.features.length === 0) && (
                    <>
                      {plan.includes_hardware && (
                        <li className="flex items-center gap-3 text-zinc-400">
                          <Check className="w-4 h-4 text-white flex-shrink-0" />
                          <span>Hardware Included ({plan.hardware_type})</span>
                        </li>
                      )}
                      {plan.includes_initial_setup && (
                        <li className="flex items-center gap-3 text-zinc-400">
                          <Check className="w-4 h-4 text-white flex-shrink-0" />
                          <span>Initial Setup Included</span>
                        </li>
                      )}
                      {plan.includes_ongoing_service && (
                        <li className="flex items-center gap-3 text-zinc-400">
                          <Check className="w-4 h-4 text-white flex-shrink-0" />
                          <span>Ongoing Service Included</span>
                        </li>
                      )}
                      {plan.setup_fee > 0 && (
                        <li className="flex items-center gap-3 text-zinc-400">
                          <Check className="w-4 h-4 text-white flex-shrink-0" />
                          <span>Setup Fee: ${plan.setup_fee}</span>
                        </li>
                      )}
                    </>
                  )}
                </ul>
                <Button 
                  onClick={() => navigate('/register')}
                  className={`mt-8 rounded-none h-12 text-sm font-semibold uppercase tracking-wide ${
                    plan.is_popular 
                      ? 'bg-white text-black hover:bg-zinc-200' 
                      : 'bg-transparent border border-zinc-700 text-white hover:bg-zinc-900'
                  }`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}

// Testimonials Section
function TestimonialsSection() {
  const testimonials = [
    {
      quote: "TortLab transformed how we handle customer support. Our response time dropped by 80% and customer satisfaction is at an all-time high.",
      author: "Sarah Chen",
      role: "Head of Customer Success",
      company: "TechFlow Inc."
    },
    {
      quote: "The multi-tenant architecture is perfect for our agency. We manage 50+ client chatbots from a single dashboard.",
      author: "Marcus Rodriguez",
      role: "Agency Director",
      company: "Digital Solutions Co."
    },
    {
      quote: "Setup was incredibly fast. We went from zero to a fully functional AI agent in under 30 minutes.",
      author: "Emily Watson",
      role: "Operations Manager",
      company: "Retail Partners"
    }
  ];

  return (
    <section className="bg-black py-24 md:py-32 border-t border-zinc-900" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-4">Testimonials</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold text-white tracking-tight">
            Loved by businesses
          </h2>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div 
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-zinc-900/30 border border-zinc-800 p-8 hover:border-zinc-700 transition-colors"
              data-testid={`testimonial-${index}`}
            >
              <p className="text-zinc-300 text-lg leading-relaxed mb-6">"{testimonial.quote}"</p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-zinc-500" />
                </div>
                <div>
                  <p className="font-semibold text-white">{testimonial.author}</p>
                  <p className="text-sm text-zinc-500">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// FAQ Section
function FAQSection() {
  const faqs = [
    {
      question: "How quickly can I deploy an AI agent?",
      answer: "Most users have their first AI agent live within 15-30 minutes. Our wizard guides you through the setup process, and no coding is required."
    },
    {
      question: "Can I customize the AI's responses?",
      answer: "Absolutely. You can configure the agent's personality, response style (short, normal, or fun), business context, and even set guardrails for what topics the AI can discuss."
    },
    {
      question: "Is my data secure?",
      answer: "Yes. We use enterprise-grade security with role-based access control, data encryption, and complete tenant isolation for agencies managing multiple clients."
    },
    {
      question: "What integrations do you support?",
      answer: "TortLab integrates with popular CRMs, helpdesk platforms, and communication tools. We also provide API access for custom integrations on our Enterprise plan."
    },
    {
      question: "Can I try before I buy?",
      answer: "Yes! All plans come with a 14-day free trial. No credit card required. You can test all features and deploy agents immediately."
    },
    {
      question: "How does billing work?",
      answer: "We offer monthly and annual billing options. Annual plans come with a 20% discount. You can upgrade, downgrade, or cancel anytime."
    }
  ];

  return (
    <section id="faq" className="bg-zinc-950 py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-4">FAQ</p>
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold text-white tracking-tight">
            Frequently asked questions
          </h2>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-zinc-800 bg-black/20 px-6"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="text-left text-white hover:text-zinc-300 font-medium py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}

// Footer Section
function Footer() {
  const navigate = useNavigate();
  
  const footerLinks = {
    product: [
      { name: 'Features', href: '#features' },
      { name: 'Pricing', href: '#pricing' },
      { name: 'FAQ', href: '#faq' },
    ],
    company: [
      { name: 'About', href: '#' },
      { name: 'Blog', href: '#' },
      { name: 'Jobs', href: '#' },
    ],
    legal: [
      { name: 'Privacy', href: '#' },
      { name: 'Terms', href: '#' },
    ]
  };

  return (
    <footer className="bg-black border-t border-zinc-900 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png" 
                alt="TortLab" 
                className="w-8 h-8 object-contain"
              />
              <span className="font-['Outfit'] font-bold text-xl text-white">TortLab</span>
            </Link>
            <p className="text-zinc-500 text-sm">
              The operating system for AI agencies.
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-zinc-500 hover:text-white transition-colors text-sm">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-zinc-500 hover:text-white transition-colors text-sm">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-zinc-500 hover:text-white transition-colors text-sm">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-zinc-900 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-600 text-sm">
            © {new Date().getFullYear()} TortLab. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/login')}
              variant="ghost" 
              className="text-zinc-500 hover:text-white text-sm"
            >
              Login
            </Button>
            <Button 
              onClick={() => navigate('/register')}
              className="bg-white text-black hover:bg-zinc-200 rounded-none h-10 px-6 text-sm font-semibold"
            >
              Get Started
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="dark bg-black min-h-screen">
      <Navbar />
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <FAQSection />
      <Footer />
    </div>
  );
}
